import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

import { api } from "../lib/api";
import {
  useActiveLanguageIsUnseeded,
  useLanguageCode,
} from "../lib/activeLanguage";
import { requireAuth } from "../lib/requireAuth";
import { UnseededLanguageLanding } from "../lib/UnseededLanguageLanding";
import { Pagination, PageMeta } from "../components/Pagination";
import { FilterSelect } from "../components/FilterSelect";
import { PageEyebrow } from "../components/PageEyebrow";
import { Centered } from "../components/Centered";

// Search params live in the URL — bookmarkable, shareable, survive
// refresh, and back-button steps through filter history. TanStack
// Router validates the search on every navigation.
// `| undefined` on each field is intentional with the
// tsconfig `exactOptionalPropertyTypes: true` — it lets the
// setSearch patch pass `field: undefined` to clear a filter
// (instead of having to omit the key conditionally).
type BrowseSearch = {
  type?: ExerciseType | undefined;
  cefr?: CefrLevel | undefined;
  skill?: string | undefined;
  offset?: number | undefined;
};

export const Route = createFileRoute("/browse")({
  beforeLoad: ({ context, location }) =>
    requireAuth(context.queryClient, location.href),
  component: BrowsePage,
  validateSearch: (raw: Record<string, unknown>): BrowseSearch => {
    const t = String(raw.type ?? "");
    const c = String(raw.cefr ?? "");
    const s = String(raw.skill ?? "");
    const o = Number(raw.offset ?? 0);
    return {
      ...(isExerciseType(t) ? { type: t } : {}),
      ...(isCefr(c) ? { cefr: c } : {}),
      ...(s ? { skill: s } : {}),
      ...(Number.isFinite(o) && o > 0 ? { offset: o } : {}),
    };
  },
});

const PAGE_SIZE = 20;

type ExerciseType =
  | "multiple_choice"
  | "translate"
  | "fill_blank"
  | "match"
  | "true_false"
  | "contrast"
  | "order"
  | "error_correct"
  | "transform"
  | "dialogue"
  | "cloze_passage";

const EXERCISE_TYPES: readonly ExerciseType[] = [
  "multiple_choice",
  "translate",
  "fill_blank",
  "match",
  "true_false",
  "contrast",
  "order",
  "error_correct",
  "transform",
  "dialogue",
  "cloze_passage",
];

type CefrLevel = "A1" | "A2" | "B1" | "B2";

const CEFR_LEVELS: readonly CefrLevel[] = ["A1", "A2", "B1", "B2"];

function isExerciseType(s: string): s is ExerciseType {
  return (EXERCISE_TYPES as readonly string[]).includes(s);
}
function isCefr(s: string): s is CefrLevel {
  return (CEFR_LEVELS as readonly string[]).includes(s);
}

const TYPE_LABELS: Record<ExerciseType, string> = {
  multiple_choice: "Multiple choice",
  translate: "Translate",
  fill_blank: "Fill the blank",
  match: "Match pairs",
  true_false: "True / false",
  contrast: "Two-slot contrast",
  order: "Order the words",
  error_correct: "Correct the error",
  transform: "Transform",
  dialogue: "Dialogue",
  cloze_passage: "Cloze passage",
};

function labelType(t: string): string {
  return (TYPE_LABELS as Record<string, string>)[t] ?? t.replace(/_/g, " ");
}

function describeDifficulty(d: number): string {
  if (d < 0.33) return "easy";
  if (d < 0.66) return "medium";
  return "hard";
}

function BrowsePage() {
  const language = useLanguageCode();
  const unseeded = useActiveLanguageIsUnseeded();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  if (unseeded) return <UnseededLanguageLanding />;

  const type = search.type ?? "";
  const cefr = search.cefr ?? "";
  const skill = search.skill ?? "";
  const offset = search.offset ?? 0;

  const setSearch = (patch: Partial<BrowseSearch>) => {
    navigate({
      search: (prev) => ({ ...prev, ...patch }),
      replace: true,
    });
  };

  const browseQ = useQuery({
    queryKey: ["exercises-browse", language, type, cefr, skill, offset],
    queryFn: async () => {
      const { data, error } = await api.GET("/exercises", {
        params: {
          query: {
            language,
            ...(type ? { type } : {}),
            ...(cefr ? { cefr } : {}),
            ...(skill ? { skill } : {}),
            limit: PAGE_SIZE,
            offset,
          },
        },
      });
      if (error) throw error;
      return data;
    },
    // Pagination feels instant: previous page stays rendered while
    // the next page's query is in flight.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const skillsQ = useQuery({
    queryKey: ["skills-for-browse-filter", language],
    queryFn: async () => {
      const { data, error } = await api.GET("/skills", {
        params: { query: { language } },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });

  if (browseQ.isLoading) return <Centered>Loading exercises…</Centered>;
  if (browseQ.isError || !browseQ.data)
    return <Centered>Could not load exercises.</Centered>;

  const { items, total } = browseQ.data;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Map skill codes → human names using the same list that powers
  // the filter dropdown. Falls back to the code if the lookup misses
  // (race during initial load, or a stale exercise that references a
  // retired skill).
  const skillName = new Map<string, string>(
    (skillsQ.data ?? []).map((s) => [s.code, s.name]),
  );

  return (
    <section className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      <PageEyebrow
        label="Reference"
        mono={`${language}.exercises`}
        count={`${total} ${total === 1 ? "item" : "items"}`}
      />
      <h2 className="font-serif text-3xl text-ink-900 mb-2 tracking-tight">
        Browse exercises
      </h2>
      <p className="text-ink-500 text-sm mb-5 sm:mb-6 max-w-xl">
        Read-only. Nothing here affects your mastery.
      </p>
      <div className="hairline mb-6" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <FilterSelect
          label="Type"
          value={type}
          placeholder="All types"
          onChange={(v) => {
            setSearch({
              type: isExerciseType(v) ? v : undefined,
              offset: 0,
            });
          }}
          options={EXERCISE_TYPES.map((t) => ({
            value: t,
            label: labelType(t),
          }))}
        />
        <FilterSelect
          label="CEFR"
          value={cefr}
          placeholder="All levels"
          onChange={(v) => {
            setSearch({
              cefr: isCefr(v) ? v : undefined,
              offset: 0,
            });
          }}
          options={CEFR_LEVELS as readonly string[]}
        />
        <FilterSelect
          label="Skill"
          value={skill}
          placeholder="All skills"
          onChange={(v) => {
            setSearch({
              skill: v ? v : undefined,
              offset: 0,
            });
          }}
          options={(skillsQ.data ?? []).map((s) => ({
            value: s.code,
            label: s.name,
          }))}
        />
      </div>

      {total > 0 && (
        <PageMeta
          from={offset + 1}
          to={Math.min(offset + items.length, total)}
          total={total}
          noun={total === 1 ? "exercise" : "exercises"}
        />
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPrev={() => setSearch({ offset: Math.max(0, offset - PAGE_SIZE) })}
        onNext={() => setSearch({ offset: offset + PAGE_SIZE })}
        isFetching={browseQ.isFetching}
        ariaLabel="Browse pagination (top)"
        className="mb-3"
      />

      <ul className="divide-y divide-ink-100 border border-ink-100 bg-paper">
        {items.map((ex) => (
          <li
            key={ex.id}
            className="px-3 sm:px-4 py-4 hover:bg-ink-50 transition-colors"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mb-2">
              <div className="font-serif text-ink-900 min-w-0 break-words">
                {ex.prompt}
              </div>
              <div className="eyebrow flex items-center gap-2 flex-wrap shrink-0">
                <span>{labelType(ex.type)}</span>
                <span aria-hidden className="text-ink-300">
                  ·
                </span>
                <span
                  title={`IRT difficulty ${ex.difficulty.toFixed(2)} (0 easiest, 1 hardest)`}
                >
                  {describeDifficulty(ex.difficulty)}
                </span>
              </div>
            </div>
            <div className="text-sm text-ink-700">
              <span className="text-ink-400">Expected:</span>{" "}
              <code className="font-mono text-ink-700 bg-ink-50 border border-ink-100 px-1.5 py-0.5">
                {ex.expected}
              </code>
            </div>
            {Array.isArray(
              (ex.metadata as Record<string, unknown>)?.options,
            ) && (
              <div className="text-sm text-ink-700 mt-1 flex flex-wrap items-baseline gap-1">
                <span className="text-ink-400">Options:</span>{" "}
                {((ex.metadata as { options: unknown[] }).options as unknown[])
                  .map(String)
                  .map((opt) => (
                    <code
                      key={opt}
                      className="font-mono text-ink-700 bg-ink-50 border border-ink-100 px-1.5 py-0.5"
                    >
                      {opt}
                    </code>
                  ))}
              </div>
            )}
            {typeof (ex.metadata as { hint?: unknown })?.hint === "string" && (
              <div className="text-sm text-ink-400 mt-1 italic">
                Hint: {String((ex.metadata as { hint: unknown }).hint)}
              </div>
            )}
            <div
              className="text-xs text-ink-400 mt-2"
              title={ex.skillCodes.join(", ")}
            >
              <span className="eyebrow mr-1">Tests</span>
              {ex.skillCodes.map((c) => skillName.get(c) ?? c).join(" · ")}
            </div>
          </li>
        ))}
      </ul>

      <Pagination
        page={page}
        totalPages={totalPages}
        onPrev={() => setSearch({ offset: Math.max(0, offset - PAGE_SIZE) })}
        onNext={() => setSearch({ offset: offset + PAGE_SIZE })}
        isFetching={browseQ.isFetching}
        ariaLabel="Browse pagination"
      />
    </section>
  );
}
